import { DecisionDetailStatusCode } from '@/components/pages/Applicant/Applicant.types';
import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { generatePath } from 'react-router-dom';
import { APIRoutes } from '../../routes';
import apiInstance from '../index';

// No StringEnumConverter on the backend DTO, so these are plain integers on the wire — not the same as the app-wide string ProductNames enum.
export enum ConditionDefinitionProduct {
    Unknown = 0,
    Factoring = 1,
    LoadPay = 2,
    TcsFuel = 3,
}

export const CONDITION_DEFINITION_PRODUCT_NAME: Record<
    ConditionDefinitionProduct,
    ProductNames | null
> = {
    [ConditionDefinitionProduct.Unknown]: null,
    [ConditionDefinitionProduct.Factoring]: ProductNames.FACTORING,
    [ConditionDefinitionProduct.LoadPay]: ProductNames.LOADPAY,
    [ConditionDefinitionProduct.TcsFuel]: ProductNames.TCSFUEL,
};

export enum ConditionTriggerQualifier {
    Unknown = 0,
    CarrierOnly = 1,
    DriverOnly = 2,
    PerOwner = 3,
}

/** A product status at which the backend adds a coded condition automatically. */
export interface ConditionDefinitionTrigger {
    product: ConditionDefinitionProduct;
    status: DecisionDetailStatusCode;
    qualifier: ConditionTriggerQualifier | null;
}

export interface ConditionDefinitionTriggerInput {
    product: ConditionDefinitionProduct;
    status: DecisionDetailStatusCode;
}

/** The statuses a user may pick as a product's trigger on an uncoded template. */
export interface ConditionDefinitionTriggerOption {
    product: ConditionDefinitionProduct;
    statuses: DecisionDetailStatusCode[];
}

export interface ConditionDefinitionDto {
    id: string;
    code: string | null;
    label: string;
    descriptionTemplate: string;
    isPerOwner: boolean;
    hasAutoComment: boolean;
    defaultProducts: ConditionDefinitionProduct[];
    referenceProducts: ConditionDefinitionProduct[];
    reviewTeams?: number[];
    /** Empty when nothing adds the template automatically; absent before the ORIG-1446 backend. */
    triggers?: ConditionDefinitionTrigger[];
    isActive: boolean;
    createdBy: string;
    modifiedBy: string;
    dateCreated: string;
    dateModified: string;
    rowVersion: number;
}

export interface CreateConditionDefinitionRequest {
    label: string;
    descriptionTemplate: string;
    defaultProducts: ConditionDefinitionProduct[];
    referenceProducts: ConditionDefinitionProduct[];
    reviewTeams: number[];
    /** null leaves the stored triggers unchanged; otherwise the full set, and any product not listed becomes Manual. */
    triggers?: ConditionDefinitionTriggerInput[] | null;
}

export interface UpdateConditionDefinitionRequest {
    label: string;
    descriptionTemplate: string;
    defaultProducts: ConditionDefinitionProduct[];
    referenceProducts: ConditionDefinitionProduct[];
    reviewTeams: number[];
    isActive: boolean;
    rowVersion: number;
    /** null leaves the stored triggers unchanged; otherwise the full set, and any product not listed becomes Manual. */
    triggers?: ConditionDefinitionTriggerInput[] | null;
}

export interface ConditionDefinitionPreviewResponse {
    renderedText: string;
    validationErrors: string[];
}

export const getConditionDefinitions = async (): Promise<ConditionDefinitionDto[]> =>
    (await apiInstance.get<ConditionDefinitionDto[]>(APIRoutes.CONDITION_DEFINITIONS)).data;

export const getConditionDefinitionPlaceholders = async (): Promise<string[]> =>
    (await apiInstance.get<string[]>(APIRoutes.CONDITION_DEFINITIONS_PLACEHOLDERS)).data;

export const getConditionDefinitionTriggerOptions = async (): Promise<
    ConditionDefinitionTriggerOption[]
> =>
    (
        await apiInstance.get<ConditionDefinitionTriggerOption[]>(
            APIRoutes.CONDITION_DEFINITIONS_TRIGGER_OPTIONS,
        )
    ).data;

export const createConditionDefinition = async (
    request: CreateConditionDefinitionRequest,
): Promise<ConditionDefinitionDto> =>
    (await apiInstance.post<ConditionDefinitionDto>(APIRoutes.CONDITION_DEFINITIONS, request)).data;

export const updateConditionDefinition = async (
    id: string,
    request: UpdateConditionDefinitionRequest,
): Promise<ConditionDefinitionDto> => {
    const endpointUrl = generatePath(APIRoutes.CONDITION_DEFINITION_BY_ID, { id });
    return (await apiInstance.put<ConditionDefinitionDto>(endpointUrl, request)).data;
};

export const previewConditionDefinition = async (
    descriptionTemplate: string,
    isPerOwner: boolean,
): Promise<ConditionDefinitionPreviewResponse> =>
    (
        await apiInstance.post<ConditionDefinitionPreviewResponse>(
            APIRoutes.CONDITION_DEFINITIONS_PREVIEW,
            { descriptionTemplate, isPerOwner },
        )
    ).data;
